import type { ReactNode } from 'react';

import { CodeView } from '../../shared/ui/code-view';
import { FieldLabel } from '../../shared/ui/field-label';
import { Hint } from '../../shared/ui/hint';
import { Note } from '../../shared/ui/note';
import {
  durationOfWords,
  loggedError,
  megabytes,
  memoryWords,
  runOutcome,
  RunOutcomeBadge,
  type TDryRun,
  type TDryRunConsoleLine,
} from '../../entities/transform';

// A run that logged an error is still a run: the verdict comes from `status` alone, never from console.error.

const Fact = ({ name, children }: { name: string; children: ReactNode }) => (
  <div className="flex justify-between gap-4 border-b border-border-row py-label">
    <span className="text-meta text-muted">{name}</span>
    <span className="font-mono text-meta text-primary">{children}</span>
  </div>
);

const LEVEL_CLASS: Record<TDryRunConsoleLine['level'], string> = {
  log: 'text-faint',
  warn: 'text-motion',
  error: 'text-danger',
};

const Console = ({ lines, truncated }: { lines: TDryRunConsoleLine[]; truncated: boolean }) => (
  <div className="mt-4">
    <FieldLabel>Console</FieldLabel>
    <div className="rounded-panel bg-surface-sunken px-3 py-3">
      {lines.map((line, index) => (
        <p key={index} className="flex gap-inset font-mono text-meta leading-20">
          <span className={`w-col-try shrink-0 ${LEVEL_CLASS[line.level]}`}>{line.level}</span>
          <span className="min-w-0 break-words text-primary">{line.message}</span>
        </p>
      ))}
    </div>
    {truncated && (
      <Hint>
        The log was cut: the sandbox keeps the first 100 entries and 16 kB, and this run wrote past
        one of them
      </Hint>
    )}
  </div>
);

export const RunResult = ({ run }: { run: TDryRun }) => {
  const outcome = runOutcome(run);
  const wrote = loggedError(run);

  return (
    <div>
      {run.output !== undefined && (
        <div className="mb-4">
          <FieldLabel>What came out</FieldLabel>
          <CodeView
            value={JSON.stringify(run.output, null, 2)}
            className="max-h-facets rounded-panel"
          />
        </div>
      )}

      <div>
        <Fact name="Finished">
          <span className="inline-flex items-center gap-3">
            <RunOutcomeBadge outcome={outcome} loggedError={wrote} />
            <span className="text-muted">{durationOfWords(run)}</span>
          </span>
        </Fact>
        <Fact name="Memory">{memoryWords(run.peakMemoryBytes, run.memoryBytes)}</Fact>
        <Fact name="Code">
          {run.codeBytes.toLocaleString('en-US')} B of {run.maxCodeBytes.toLocaleString('en-US')}
        </Fact>
        <Fact name="Clock inside">{new Date(run.frozenNow).toISOString()}</Fact>
      </div>

      <Hint>
        The server&rsquo;s own ceilings — {run.wallClockMs.toLocaleString('en-US')} ms and{' '}
        {megabytes(run.memoryBytes)} MB. A step or rule may hold this code to less, and a rehearsal
        does not apply that
      </Hint>

      {run.error && (
        <Note tone="danger" className="mt-4">
          <p className="font-mono text-primary">{run.error.code}</p>
          <p className="mt-2">{run.error.message}</p>
          {run.error.line !== null && run.error.line !== undefined && (
            <p className="mt-2 font-mono text-muted">
              line {run.error.line}
              {run.error.column !== null && run.error.column !== undefined
                ? `, column ${run.error.column}`
                : ''}
            </p>
          )}
        </Note>
      )}

      {run.diff &&
        (run.diff.added.length > 0 ||
          run.diff.removed.length > 0 ||
          run.diff.changed.length > 0) && (
          <div className="mt-4">
            <FieldLabel>What it moved</FieldLabel>
            <p className="font-mono text-meta leading-20 text-muted">
              {run.diff.added.length} added · {run.diff.removed.length} removed ·{' '}
              {run.diff.changed.length} changed
            </p>
            <Hint>{[...run.diff.added, ...run.diff.changed].slice(0, 4).join(', ')}</Hint>
          </div>
        )}

      {run.console.length > 0 && <Console lines={run.console} truncated={run.consoleTruncated} />}

      <Hint>{outcome.meaning}</Hint>
    </div>
  );
};
